// Single de Grammar Bank: 2 tabs Learn / Activities. Admin: botón "Editar".

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { Spinner } from '@/components/atoms/Spinner';
import { Header } from '@/components/organisms/Header';
import { GrammarBankEditorDrawer } from '@/components/organisms/GrammarBankEditorDrawer';
import { useAuth } from '@/context/AuthContext';
import { ApiError } from '@/services/api';
import {
  getGrammarBankBySlug,
  type GrammarBankActivity,
  type GrammarBankListItem,
} from '@/services/grammar-banks';
import { safeHtml } from '@/utils/safeHtml';
import styles from './GrammarBank.module.scss';

type TabKey = 'learn' | 'activities';

export function GrammarBankDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { state } = useAuth();
  const isAdmin = state.status === 'authenticated' && state.user.roles.includes('admin');

  const [item, setItem] = useState<GrammarBankListItem | null>(null);
  const [activities, setActivities] = useState<GrammarBankActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('learn');
  const [editorOpen, setEditorOpen] = useState(false);

  const reload = useCallback(async () => {
    if (!slug) {
      setError('Slug inválido');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const d = await getGrammarBankBySlug(slug);
      setItem(d.item);
      setActivities(d.activities);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <>
      <Header
        title={item?.title ?? 'Grammar Bank'}
        actions={
          isAdmin && item ? (
            <Button variant="secondary" onClick={() => setEditorOpen(true)}>
              <Icon name="book" size={14} /> Editar
            </Button>
          ) : null
        }
      />
      <main className={styles.main}>
        <Link to="/grammar-bank" className={styles.backLink}>
          <Icon name="chevron-left" size={16} /> Volver
        </Link>

        {loading ? (
          <div className={styles.center}>
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <p className={styles.error}>{error}</p>
        ) : !item ? null : (
          <>
            <header className={styles.singleHeader}>
              <div className={styles.singleBadges}>
                {item.level_label && <Badge tone="accent">{item.level_label}</Badge>}
                {item.category_label && <Badge tone="neutral">{item.category_label}</Badge>}
                {item.is_published === 0 && <Badge tone="warning">Borrador</Badge>}
              </div>
              <h2 className={styles.singleTitle}>{item.title}</h2>
            </header>

            <div className={styles.tabs} role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'learn'}
                className={`${styles.tab} ${tab === 'learn' ? styles.tabActive : ''}`}
                onClick={() => setTab('learn')}
              >
                Learn
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'activities'}
                className={`${styles.tab} ${tab === 'activities' ? styles.tabActive : ''}`}
                onClick={() => setTab('activities')}
              >
                Activities ({activities.length})
              </button>
            </div>

            {tab === 'learn' ? (
              item.content ? (
                <article
                  className={styles.learnHtml}
                  dangerouslySetInnerHTML={{ __html: safeHtml(item.content) }}
                />
              ) : (
                <p className={styles.error}>Esta ficha aún no tiene contenido de teoría.</p>
              )
            ) : activities.length === 0 ? (
              <div className={styles.empty}>
                <h3>Sin actividades</h3>
                <p>{isAdmin ? 'Añade actividades desde el botón "Editar".' : 'Esta ficha aún no tiene ejercicios.'}</p>
              </div>
            ) : (
              <ul className={styles.activitiesList}>
                {activities.map((a) => (
                  <li key={a.id} className={styles.activityRow}>
                    <span className={styles.activityNum}>{String(a.position).padStart(2, '0')}</span>
                    <div className={styles.activityBody}>
                      {a.statement_label && <span className={styles.activityStatement}>{a.statement_label}</span>}
                      {a.content && <span className={styles.activityContent}>{a.content}</span>}
                      <span className={styles.activityContent}>
                        {a.quiz_title ? `Quiz: ${a.quiz_title}` : 'Sin quiz vinculado'}
                      </span>
                    </div>
                    {a.quiz_id ? (
                      <Button asChild variant="primary" size="sm">
                        <Link to={`/quiz/${a.quiz_id}?grammar=${item.id}`}>Empezar</Link>
                      </Button>
                    ) : (
                      <Badge tone="warning">Sin quiz</Badge>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </main>

      {isAdmin && item && (
        <GrammarBankEditorDrawer
          open={editorOpen}
          onOpenChange={setEditorOpen}
          mode="edit"
          item={item}
          activities={activities}
          onSaved={() => {
            setEditorOpen(false);
            void reload();
          }}
        />
      )}
    </>
  );
}
